// MessageInput.jsx
import React, { useState, useRef, useEffect, useCallback } from 'react';
import fetchApi from '../../../../../Lib/api';
import useUserStore from '../../../../../../stores/userStore';

// --- Import Icon Components ---
import {
    CloseIcon,
    PaperClipIcon,
    FaceSmileIcon,
    MicrophoneIcon,
    StopIcon,
    PaperAirplaneIcon,
    ArrowPathIcon,
    DeleteIcon,
    PlusIcon
} from './ChatIcons'; // Adjust path if needed

const bytesToMB = (bytes) => (bytes / (1024 * 1024)).toFixed(0);

const LoadingSpinner = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="animate-spin text-indigo-600 dark:text-indigo-400 mr-2"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"></path></svg>
);

const ProgressBar = ({ progress }) => (
   <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1.5 dark:bg-gray-700">
       <div className="bg-indigo-600 h-1.5 rounded-full transition-all duration-300 ease-out" style={{ width: `${progress}%` }}></div>
   </div>
);

const EmojiPicker = ({ onSelect, onClose }) => {
    const emojis = [
      '😀', '😃', '😄', '😁', '😊', '😍', '🥰', '😘', '😗', '😙',
    ];
  
    return (
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg p-3 w-64 max-h-48 overflow-y-auto">
        <div className="grid grid-cols-8 gap-1">
          {emojis.map((emoji, index) => (
            <button
              key={index}
              onClick={() => onSelect(emoji)}
              className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-lg transition-colors duration-150"
              title={emoji}
            >
              {emoji}
            </button>
          ))}
        </div>
      </div>
    );
  };
  

// --- Helper: Get Icon based on type ---
// NOTE: Requires actual icon components (FileImageIcon, FilePdfIcon, etc.) in ChatIcons.jsx
const AttachmentIcon = ({ type }) => {
    // Simplified logic - replace with actual icon components when available
     if (type === 'image' || (typeof type === 'string' && type.startsWith('image/'))) {
         // Replace with e.g., <FileImageIcon className="w-5 h-5 text-pink-500" />
         return ( <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" aria-hidden="true" className="w-5 h-5 text-pink-500"> <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25z" /> </svg> );
     }
     if (type === 'pdf' || type === 'application/pdf') {
         // Replace with e.g., <FilePdfIcon className="w-5 h-5 text-red-500" />
         return ( <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" aria-hidden="true" className="w-5 h-5 text-red-500"> <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" /> </svg> );
     }
      if (type === 'video' || (typeof type === 'string' && type.startsWith('video/'))) {
         // Replace with e.g., <FileVideoIcon className="w-5 h-5 text-purple-500" />
         return ( <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" aria-hidden="true" className="w-5 h-5 text-purple-500"> <path strokeLinecap="round" d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z" /> </svg> );
     }
     // Default document icon
     // Replace with e.g., <FileDocumentIcon className="w-5 h-5 text-gray-500" />
     return ( <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" aria-hidden="true" className="w-5 h-5 text-gray-500"> <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m.75 12l3 3m0 0l3-3m-3 3v-6m-1.5-9H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" /> </svg> );
};


const MessageInput = ({
    onSendMessage,
    replyingToMessage = null,
    onCancelReply = () => {},
    onStartTyping = () => {},
    onStopTyping = () => {},
    conversationId, // Need conversationId for signed URL context
}) => {
  // State Hooks
  const [messageText, setMessageText] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showAttachmentModal, setShowAttachmentModal] = useState(false);
  // --- attachments state now includes upload status/progress ---
  const [attachments, setAttachments] = useState([]);
  const [attachmentCaption, setAttachmentCaption] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  // --- State to track if the modal's "Send" button is busy (uploading) ---
  const [isSendingAttachments, setIsSendingAttachments] = useState(false); // Manage local sending state


  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [recordingDuration, setRecordingDuration] = useState(0);


  // Ref Hooks
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const isTypingRef = useRef(false);

  const recordingIntervalRef = useRef(null);
  const { user: userPayload, setUser } = useUserStore((state) => ({
    user: state.user,
    setUser: state.setUser,
  }));

  const storageLimitState = userPayload?.limit_states?.storage || {};
  const storageLimitReached = Boolean(storageLimitState.limit_reached);
  const storageLimitRemaining = storageLimitState.remaining;
  const storageLimitMessage = storageLimitState.limit
    ? `You've used ${bytesToMB(storageLimitState.used)}MB of ${bytesToMB(
        storageLimitState.limit
      )}MB. Delete files or upgrade to upload more.`
    : "You've reached your storage limit. Delete files or upgrade to upload more.";

  const updateStorageUsage = useCallback(
    (usage) => {
      if (!usage) return;
      setUser({
        limit_states: {
          ...(userPayload?.limit_states || {}),
          storage: usage,
        },
      });
    },
    [setUser, userPayload]
  );

  const handleStorageLimitError = useCallback(
    (payload) => {
      if (payload?.storage_usage) {
        updateStorageUsage(payload.storage_usage);
      }
      alert(payload?.error || storageLimitMessage);
    },
    [updateStorageUsage, storageLimitMessage]
  );

  // API endpoints - These are USED by MessageInput in this flow
  const signedUrlEndpoint = `/uploads/signed_url`;
  const confirmUploadEndpoint = `/uploads/confirm`;

  const handleToggleRecording = async () => {
    if (!isRecording) {
      if (storageLimitReached) {
        alert(storageLimitMessage);
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const chunks = [];
        const recorder = new MediaRecorder(stream);
  
        recorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            chunks.push(event.data);
          }
        };
  
        recorder.onstop = () => {
          const audioBlob = new Blob(chunks, { type: 'audio/webm;codecs=opus' });
          const audioFile = new File([audioBlob], `voice-message-${Date.now()}.webm`, { 
            type: 'audio/webm;codecs=opus' 
          });
  
          stream.getTracks().forEach(track => track.stop());
  
          if (storageLimitReached) {
            alert(storageLimitMessage);
          } else {
            const newAttachment = {
              id: `audio-${Date.now()}`,
              name: audioFile.name,
              size: audioFile.size,
              displaySize: `${(audioFile.size / 1024).toFixed(1)} KB`,
              type: audioFile.type,
              fileData: audioFile,
              status: 'pending',
              progress: 0,
              error: null,
              fileKey: null,
              backendId: null,
              url: null,
              uploadXhr: null,
            };
            setAttachments((prev) => [...prev, newAttachment]);
            setShowAttachmentModal(true);
          }
          
          setMediaRecorder(null);
          setRecordingDuration(0);
          signalStopTyping();
          if (onCancelReply) onCancelReply();

          if (recordingIntervalRef.current) {
            clearInterval(recordingIntervalRef.current);
            recordingIntervalRef.current = null;
          }
        };
  
        recorder.start();
        setMediaRecorder(recorder);
        setIsRecording(true);
        
        const startTime = Date.now();
        if (recordingIntervalRef.current) {
            clearInterval(recordingIntervalRef.current);
        }
        recordingIntervalRef.current = setInterval(() => {
          setRecordingDuration(Math.floor((Date.now() - startTime) / 1000));
        }, 1000);
  
        signalStopTyping();
      } catch (error) {
        console.error('Error accessing microphone:', error);
        alert('Unable to access microphone. Please check your permissions.');
      }
    } else {
      if (mediaRecorder && mediaRecorder.state === 'recording') {
        mediaRecorder.stop();
      }
      setIsRecording(false);
      
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
        recordingIntervalRef.current = null;
      }
    }
  };
  
  // Add this helper function for formatting recording duration
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };


  // --- Auto-resize Textarea ---
  const autoResizeTextarea = useCallback(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'; // Reset height
      const scrollHeight = textareaRef.current.scrollHeight;
      const maxHeight = 80; // Approx 5rem in pixels (adjust based on your styling)
      textareaRef.current.style.height = `${Math.min(scrollHeight, maxHeight)}px`;
    }
  }, []);

  useEffect(() => {
    autoResizeTextarea();
  }, [messageText, autoResizeTextarea]);

  // --- Effect for Emoji Picker ---
   useEffect(() => {
     const handleClickOutside = (event) => {
       if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
          const toggleButton = event.target.closest('button[aria-label="toggle emoji picker"]');
          if(!toggleButton) { setShowEmojiPicker(false); }
       }
     };
     if (showEmojiPicker) { document.addEventListener('mousedown', handleClickOutside); }
     else { document.removeEventListener('mousedown', handleClickOutside); }
     return () => document.removeEventListener('mousedown', handleClickOutside);
   }, [showEmojiPicker]);

   // --- Cleanup Typing Timer on Unmount ---
    useEffect(() => {
        return () => {
            clearTimeout(typingTimeoutRef.current);
            // Optionally: If component unmounts while user was marked as typing, notify stop
            if (isTypingRef.current) {
                 onStopTyping();
                 isTypingRef.current = false;
            }
        };
    }, [onStopTyping]); // Include onStopTyping in dependency array


  // --- Event Handlers ---

  // Typing Indicators Logic
  const handleInputChange = (event) => {
      const newText = event.target.value;
      setMessageText(newText);

      clearTimeout(typingTimeoutRef.current);

      if (newText.trim() && !isTypingRef.current) {
          onStartTyping();
          isTypingRef.current = true;
      }

      typingTimeoutRef.current = setTimeout(() => {
          if (isTypingRef.current) {
              onStopTyping();
              isTypingRef.current = false;
          }
      }, 1500);
  };

  const signalStopTyping = useCallback(() => {
      clearTimeout(typingTimeoutRef.current);
      if (isTypingRef.current) {
          onStopTyping();
          isTypingRef.current = false;
      }
  }, [onStopTyping]);


  const handleToggleEmojiPicker = () => { setShowEmojiPicker(prev => !prev); };
  const handleEmojiSelect = (emojiObject) => {
      const currentText = messageText;
      const cursorPosition = textareaRef.current?.selectionStart ?? currentText.length;
      const textBefore = currentText.substring(0, cursorPosition);
      const textAfter = currentText.substring(cursorPosition);
      const newText = textBefore + emojiObject + textAfter;

      setMessageText(newText);
      clearTimeout(typingTimeoutRef.current);
      if (newText.trim() && !isTypingRef.current) {
          onStartTyping();
          isTypingRef.current = true;
      }
       typingTimeoutRef.current = setTimeout(() => {
           if (isTypingRef.current) {
               onStopTyping();
               isTypingRef.current = false;
           }
       }, 1500);

      setShowEmojiPicker(false);
      textareaRef.current?.focus();
      requestAnimationFrame(() => {
        if (textareaRef.current) {
            textareaRef.current.selectionStart = textareaRef.current.selectionEnd = cursorPosition + emojiObject.length;
        }
      });
  };

  const handleAttachmentModalClose = () => {
      // If uploads are in progress, confirm with the user before closing
      if (isSendingAttachments) {
           const confirmCancel = window.confirm("Uploads are in progress. Are you sure you want to cancel?");
           if (!confirmCancel) return;
           // TODO: Implement aborting XHR uploads if user confirms cancellation
           attachments.forEach(att => {
               if (att.uploadXhr && att.uploadXhr.readyState !== 4) {
                   att.uploadXhr.abort(); // Abort the XHR
               }
           });
      }

      // Reset modal state and clear attachments
      setShowAttachmentModal(false);
      setAttachments([]); // Clear all staged/uploading/uploaded attachments from state
      setAttachmentCaption(''); // Clear caption
      setIsSendingAttachments(false); // Reset local sending state
      if (fileInputRef.current) fileInputRef.current.value = null; // Reset file input
      signalStopTyping(); // Ensure typing indicator stops
  };

  const handleAddAttachmentClick = () => {
      if (storageLimitReached) {
        alert(storageLimitMessage);
        return;
      }
      if (!showAttachmentModal) {
          setAttachments([]);
          setAttachmentCaption('');
       }
      fileInputRef.current?.click();
  };

  // handleFileSelected - Add files to state with initial upload status
  const handleFileSelected = (event) => {
      const files = Array.from(event.target.files);
      if (files.length === 0) return;

      if (storageLimitState.limit && typeof storageLimitRemaining === 'number') {
        let remaining = storageLimitRemaining;
        const blocked = files.some((file) => {
          if (remaining <= 0 || file.size > remaining) {
            return true;
          }
          remaining -= file.size;
          return false;
        });

        if (blocked) {
          alert(storageLimitMessage);
          if (fileInputRef.current) fileInputRef.current.value = null;
          return;
        }
      }

      const newAttachments = files.map(file => ({
        id: `${file.name}-${file.lastModified}-${file.size}`, // Temporary client-side ID
        name: file.name,
        size: file.size, // Keep original size in bytes
        displaySize: `${(file.size / 1024).toFixed(1)} KB`, // Size string for display
        type: file.type || 'unknown',
        fileData: file, // --- Keep the original File object for upload ---
        status: 'pending', // --- Initial upload status ---
        progress: 0, // --- Initial progress ---
        error: null, // --- Error state ---
        fileKey: null, // To store GCS file key
        backendId: null, // To store backend Attachment ID
        url: null, // To store final file URL
        uploadXhr: null, // To store XHR object for progress/abort
      }));

      setAttachments(prev => {
          const existingIds = new Set(prev.map(att => att.id));
          const trulyNew = newAttachments.filter(att => !existingIds.has(att.id));
          return [...prev, ...trulyNew];
      });

       if (!showAttachmentModal) {
           setShowAttachmentModal(true);
       }
      if (fileInputRef.current) fileInputRef.current.value = null;
  };

  // Helper to update attachment state by temporary ID
  const updateAttachmentState = useCallback((idToUpdate, updates) => {
      setAttachments(prev =>
          prev.map(att =>
              att.id === idToUpdate ? { ...att, ...updates } : att
          )
      );
  }, []);


  // Helper to handle GCS upload and backend confirmation for a single attachment (within MessageInput)
  const uploadAttachment = useCallback(async (attachment) => {
      // attachment is the temporary attachment object including fileData

    //   if (!conversationId) {
    //        updateAttachmentState(attachment.id, { status: 'failed', error: 'Conversation ID not available.' });
    //        return null; // Indicate failure
    //   }

      try {
        // --- Step 1: Get signed URL ---
        updateAttachmentState(attachment.id, { status: 'uploading', progress: 0, error: null });

        const signedUrlResponse = await fetchApi(
          signedUrlEndpoint,
          'POST',
          {
            filename: attachment.name,
            content_type: attachment.type,
            context_type: 'Message',
          }
        );

        const signedUrlPayload = await signedUrlResponse.json().catch(() => ({}));

        if (!signedUrlResponse.ok) {
          if (signedUrlPayload?.error_code === 'limit_exceeded') {
            handleStorageLimitError(signedUrlPayload);
          }
          updateAttachmentState(attachment.id, { status: 'failed', error: signedUrlPayload.error || 'Failed to get signed URL.' });
          throw new Error(signedUrlPayload.error || "Failed to get signed URL for upload.");
        }

        const { signed_url, file_key } = signedUrlPayload;
        updateAttachmentState(attachment.id, { fileKey: file_key });


        // --- Step 2: Upload to GCS with progress tracking ---
        const xhr = new XMLHttpRequest();
        updateAttachmentState(attachment.id, { uploadXhr: xhr });

        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            updateAttachmentState(attachment.id, { progress: Math.round((event.loaded / event.total) * 100) });
          }
        };
        xhr.onerror = () => { updateAttachmentState(attachment.id, { status: 'failed', error: 'Network error during upload.' }); };
        xhr.onabort = () => { updateAttachmentState(attachment.id, { status: 'cancelled', error: 'Upload cancelled.' }); };


        const uploadPromise = new Promise((resolve, reject) => {
          xhr.onload = async () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              updateAttachmentState(attachment.id, { progress: 100 });

              try {
                // --- Step 3: Confirm upload with backend ---
                const confirmResponse = await fetchApi(
                  confirmUploadEndpoint,
                  'POST',
                  {
                     file_key, filename: attachment.name, content_type: attachment.type, size_bytes: attachment.size,
                     attachable_type: 'Message', // Indicate it's for a Message context
                  }
                );

                const confirmPayload = await confirmResponse.json().catch(() => ({}));

                if (!confirmResponse.ok) {
                  if (confirmPayload?.error_code === 'limit_exceeded') {
                    handleStorageLimitError(confirmPayload);
                  }
                  updateAttachmentState(attachment.id, { status: 'failed', error: confirmPayload.error || 'Failed to confirm upload.' });
                  reject(new Error(confirmPayload.error || "Failed to confirm upload with backend."));
                  return;
                }

                const fileData = confirmPayload;
                updateAttachmentState(attachment.id, {
                    status: 'uploaded',
                    backendId: fileData.file.id,
                    url: fileData.file.url,
                    uploadXhr: null,
                });
                updateStorageUsage(fileData.storage_usage);
                resolve(fileData.file);

              } catch (error) {
                 updateAttachmentState(attachment.id, { status: 'failed', error: error.message || 'Confirmation failed.' });
                 reject(error);
               }
            } else {
               updateAttachmentState(attachment.id, { status: 'failed', error: `Upload failed (Status: ${xhr.status})` });
               reject(new Error(`Upload failed with status: ${xhr.status}`));
            }
          };
        });

        xhr.open("PUT", signed_url, true);
        xhr.setRequestHeader("Content-Type", attachment.type);
        xhr.send(attachment.fileData);

        return uploadPromise;

      } catch (error) {
        updateAttachmentState(attachment.id, { status: 'failed', error: error.message || 'Upload process failed.' });
        throw error;
      }
  }, [
    conversationId,
    signedUrlEndpoint,
    confirmUploadEndpoint,
    updateAttachmentState,
    fetchApi,
    storageLimitState.limit,
    storageLimitRemaining,
    storageLimitReached,
    storageLimitMessage,
    handleStorageLimitError,
    updateStorageUsage,
  ]);

  // handleReplaceAttachment - Keep placeholder
  const handleReplaceAttachment = (id) => { alert('Replace attachment functionality not implemented yet.'); /* TODO */ };

   // handleDeleteAttachment - Handle deleting staged/failed/pending files, potentially aborting uploads
   const handleDeleteAttachment = useCallback((idToDelete) => {
        setAttachments(prev => {
             const attachmentToDelete = prev.find(att => att.id === idToDelete);
             if (attachmentToDelete?.uploadXhr && attachmentToDelete.uploadXhr.readyState !== 4) {
                 attachmentToDelete.uploadXhr.abort(); // Abort the XHR
             }
            return prev.filter(att => att.id !== idToDelete);
        });
         setTimeout(() => {
             if (attachments.filter(att => att.id !== idToDelete).length === 0) {
                  handleAttachmentModalClose();
             }
         }, 0);
   }, [attachments, handleAttachmentModalClose]);


    // Handler for sending attachments - Orchestrates uploads
   const handleSendAttachments = async () => {
        // Filter for attachments that need to be uploaded (pending, failed, cancelled)
        const filesToProcess = attachments.filter(att =>
            att.status === 'pending' || att.status === 'failed' || att.status === 'cancelled'
        );

        // If no text and no files to process, return
        if (filesToProcess.length === 0 && !messageText.trim()) {
            console.log('No files staged/failed and no text. Cannot send.');
            return;
        }

        // --- Step 1: Signal local sending state ---
        setIsSendingAttachments(true); // Set local state to show spinner on modal send button
        signalStopTyping(); // Ensure typing indicator stops before sending

        // --- Step 2: Orchestrate GCS Uploads and Confirmations ---
        // Map files that need processing to upload promises
        const uploadPromises = filesToProcess.map(attachment => uploadAttachment(attachment));

        // Wait for all uploads to complete (success or failure)
        // Promise.allSettled allows us to process results even if some uploads fail
        const uploadResults = await Promise.allSettled(uploadPromises);


        // --- Step 3: Compose message payload for parent ---
        // Collect data for successfully uploaded attachments
        const successfulUploads = uploadResults
            .filter(result => result.status === 'fulfilled' && result.value && result.value.id) // Filter fulfilled promises with a backend ID
            .map(result => result.value); // Get the confirmed backend data


        // If no text AND no successful attachments/audio, handle as a failed send attempt
         const textToSend = messageText.trim();
         const hasSuccessfulAttachments = successfulUploads.length > 0;
        //  const isAudioMessage = successfulUploads.some(att => att.type.startsWith('audio')); // Check if any audio uploaded successfully

         if (!textToSend && !hasSuccessfulAttachments) { // Removed isAudioMessage from this check, rely on successfulUploads
             console.warn("No text and no successful uploads. Aborting message send.");
             // The modal will remain open showing failed statuses. User can retry or cancel.
             setIsSendingAttachments(false); // Turn off modal spinner
             return; // Stop here
         }

         console.log(successfulUploads)


        // Determine the overall message type based on what was successfully sent
        let overallMessageType = 'text';
        if (hasSuccessfulAttachments) {
             const hasNonAudioAttachment = successfulUploads.some(att => !att.content_type.startsWith('audio'));
             const hasAudioAttachment = successfulUploads.some(att => att.content_type.startsWith('audio'));

            if (hasAudioAttachment && !hasNonAudioAttachment && !textToSend) {
                 overallMessageType = 'audio'; // Pure audio message (only audio attachments, no text)
            } else {
                 overallMessageType = 'media'; // Any other attachment type, or attachments with text/caption
            }
        }


        const messagePayload = {
            type: overallMessageType, // Determined type
            // Content structure expected by parent's handleSendMessage
            content: {
                 text: textToSend || attachmentCaption || '', // Use text input or caption
                 // Pass the array of *confirmed backend* attachment data
                 attachments: successfulUploads.map(att => ({
                     // Pass necessary confirmed backend data
                     id: att.id, // Backend Attachment ID
                     name: att.name,
                     size: att.size,
                     type: att.type,
                     url: att.url, // Final file URL
                 })),
                 // If audio message, pass the single audio attachment data here if structured differently
                 // audio: isAudioMessage && overallMessageType === 'audio' ? successfulUploads.find(att => att.type.startsWith('audio')) : undefined, // Example
            },
            // Include reply info if applicable
            replyToBackendId: replyingToMessage?.backend_id || null
        };

        // --- Step 4: Call parent's onSendMessage with the final payload ---
        if (typeof onSendMessage === 'function') {
             onSendMessage(messagePayload); // Parent will create the message record
        } else { console.warn("onSendMessage prop is not a function!"); }


        // --- Step 5: Reset Input and Modal (after calling parent) ---
        // Clear text input
        setMessageText('');
        // Close modal and clear local state
        handleAttachmentModalClose(); // This will also set isSendingAttachments to false
        // Reset textarea size and focus
        if(textareaRef.current) { textareaRef.current.style.height = 'auto'; }
        // Cancel reply mode
        if (onCancelReply) onCancelReply();

        // Blob URL cleanup (if any were created for local previews)
        // This happens implicitly when handleAttachmentModalClose clears state and attachments
        // If previews used Object URLs, they are associated with the File objects in the state.
        // When state is cleared, the garbage collector *might* eventually clean up, but explicit revoke is safer.
        // Adding explicit cleanup for blob URLs:
        attachments.forEach(att => {
            if (att.url && att.url.startsWith('blob:')) URL.revokeObjectURL(att.url);
            if (att.thumbnailUrl && att.thumbnailUrl.startsWith('blob:')) URL.revokeObjectURL(att.thumbnailUrl); // If you used thumbnail URLs
        });

   };


   // Handler for sending text message (without attachments) - Simplified logic
   const handleSendMessageClick = () => {
    const textToSend = messageText.trim();
    const hasStagedAttachments = attachments.length > 0;

    if (showAttachmentModal && !isSendingAttachments) {
        handleSendAttachments();
    } else if (hasStagedAttachments && !showAttachmentModal && !isSendingAttachments) {
        setShowAttachmentModal(true);
    }
    else if (textToSend && !hasStagedAttachments && !showAttachmentModal && !isSendingAttachments) {
         if (typeof onSendMessage === 'function') {
           onSendMessage({
               type: 'text',
               content: {text: textToSend},
               replyToBackendId: replyingToMessage?.backend_id || null
           });
         } else { console.warn("onSendMessage prop is not a function!"); }

         setMessageText('');
         signalStopTyping();
         if(textareaRef.current) { textareaRef.current.style.height = 'auto'; }
          if (onCancelReply) onCancelReply();

    } else { console.log('Cannot send empty message or sending in progress.'); }
};

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault(); // Prevent newline

      // If attachment modal is open and there are files staged, and not already sending,
      // trigger the modal's send logic on Enter.
      if (showAttachmentModal && attachments.length > 0 && !isSendingAttachments) {
          handleSendAttachments(); // Trigger the send staged attachments logic
      } else {
         // Otherwise, if there's text and no files staged/modal not active/not sending, trigger standard text send.
         if (messageText.trim() && attachments.length === 0 && !showAttachmentModal && !isSendingAttachments) {
            handleSendMessageClick(); // Trigger standard text message send
         } else {
             console.log("Enter pressed but no valid message to send (text or attachments).");
         }
      }
    }
    // Optional: Handle Escape key to cancel reply or close modals
    if (event.key === 'Escape') {
        if (showAttachmentModal) { handleAttachmentModalClose(); } // Close modal (will prompt confirmation if sending)
        else if (replyingToMessage) { onCancelReply(); } // Cancel reply
        else if (showEmojiPicker) { setShowEmojiPicker(false); } // Close emoji picker
    }
  };

  useEffect(() => {
    return () => {
      // Cleanup recording interval on unmount
      if (recordingIntervalRef.current) {
        cancelAnimationFrame(recordingIntervalRef.current);
      }
      // Stop any ongoing recording
      if (mediaRecorder && mediaRecorder.state === 'recording') {
        mediaRecorder.stop();
      }
    };
  }, [mediaRecorder]);

  const ReplyPreviewBar = () => {
    if (!replyingToMessage) return null;

    // Assuming replyingToMessage now includes { id, senderName, text, backend_id }
    // Use frontend id for key if needed, but pass backend_id when sending.
    return (
         <div className="absolute top-0 left-0 right-0 z-10 w-full px-3 md:px-5 py-2 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 flex items-center justify-between transition-all duration-300">
            <div className="border-l-2 pl-3 cursor-default outline-none border-indigo-500 dark:border-indigo-400 flex-grow min-w-0 mr-3" aria-label={`Replying to: ${replyingToMessage.senderName}`}>
                 <p className="mb-0.5 font-semibold text-xs text-indigo-600 dark:text-indigo-300 leading-tight tracking-tight">
                    Reply to {replyingToMessage.senderName}
                 </p>
                 <p className="outline-none text-sm font-normal leading-snug tracking-tight text-black opacity-60 dark:text-white dark:opacity-70 truncate">
                    {replyingToMessage.text}
                 </p>
             </div>
             <div className="flex-shrink-0">
                 <button
                     onClick={onCancelReply}
                     className="group flex justify-center items-center rounded-full outline-none focus:outline-none focus:ring-1 focus:ring-indigo-400 focus:ring-offset-1 dark:focus:ring-offset-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 w-7 h-7"
                     title="Cancel reply"
                     aria-label="Cancel reply"
                  >
                     <CloseIcon className="w-4 h-4 text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-200" />
                 </button>
             </div>
        </div>
    );
};



  // --- Render JSX ---
  return (
    // ... (Main container div and ReplyPreviewBar call remain the same)
    <div className={`relative w-full border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 transition-all duration-300 ${replyingToMessage ? 'pt-[3.75rem]' : 'pt-0'}`}>

    <ReplyPreviewBar />

      {/* Input Area Container */}
      {/* Disable the entire input area if modal is open or sending */}
      <div className={`h-auto min-h-[3.25rem] px-3 py-2 md:px-5 md:py-3 flex items-end gap-2 md:gap-3 ${showAttachmentModal || isSendingAttachments ? 'opacity-50 pointer-events-none' : ''}`}> {/* Disable input area */}

        {/* Attach Button */}
        <div className="flex-shrink-0 self-end mb-[1px]">
             <input type="file" ref={fileInputRef} onChange={handleFileSelected} multiple accept="image/*,video/*,audio/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip" style={{ display: 'none' }} aria-hidden="true" disabled={storageLimitReached} />
             {/* Button to trigger file input - Disable if modal is open or sending */}
             <button
               onClick={handleAddAttachmentClick}
               className="group flex justify-center items-center rounded-full outline-none focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-indigo-400 dark:focus:ring-offset-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 w-8 h-8 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
               title={storageLimitReached ? storageLimitMessage : "Attach file"}
               aria-label="Attach file"
               disabled={showAttachmentModal || isSendingAttachments || storageLimitReached}
             >
                 <PaperClipIcon className="w-5 h-5 text-gray-500 dark:text-gray-400 group-hover:text-indigo-500 dark:group-hover:text-indigo-300" />
             </button>
        </div>

        {/* Textarea & Emoji Button Container */}
        <div className="flex-grow self-end relative">
              {/* Disable textarea if modal is open or sending */}
              <textarea ref={textareaRef} className="block w-full px-3 py-1.5 md:px-4 md:py-2 rounded-lg outline-none text-sm placeholder:text-gray-500 dark:placeholder:text-gray-400/80 focus:outline-none focus:ring-1 focus:ring-indigo-400 dark:focus:ring-indigo-500 transition duration-150 ease-out text-black dark:text-white bg-gray-100 dark:bg-gray-700/60 max-h-[5rem] pr-10 resize-none scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 hover:bg-gray-200/70 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed" value={messageText} onChange={handleInputChange} onKeyDown={handleKeyDown} onInput={autoResizeTextarea} rows="1" placeholder="Write a message..." aria-label="Write a message" style={{ overflowY: 'auto' }} disabled={showAttachmentModal || isSendingAttachments} ></textarea>
              <div className="absolute bottom-0 right-0 flex items-center h-full mr-1.5">
                {/* Disable emoji button if modal is open or sending */}
                {/* <button onClick={handleToggleEmojiPicker} className="group flex justify-center items-center rounded-full outline-none focus:outline-none hover:bg-gray-200 dark:hover:bg-gray-600 w-8 h-8 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed" title="toggle emoji picker" aria-label="toggle emoji picker" disabled={showAttachmentModal || isSendingAttachments} >
                    <FaceSmileIcon className="w-5 h-5 text-gray-500 dark:text-gray-400 group-hover:text-indigo-500 dark:group-hover:text-indigo-300" />
                </button> */}
                {/* {showEmojiPicker && (
                <div ref={emojiPickerRef} className="absolute z-10 bottom-full right-0 mb-2">
                    <EmojiPicker 
                    onSelect={handleEmojiSelect} 
                    onClose={() => setShowEmojiPicker(false)} 
                    />
                </div>
                )} */}
              </div>
        </div>

        {/* Record / Send Button Area */}
        <div className="flex-shrink-0 self-end mb-[1px]">
  {/* Show recording indicator if recording */}
  {isRecording ? (
    <div className="flex items-center mr-2">
      <div className="flex items-center bg-red-500 text-white px-3 py-1 rounded-full text-xs font-medium">
        <div className="w-2 h-2 bg-white rounded-full mr-2 animate-pulse"></div>
        {formatDuration(recordingDuration)}
      </div>
    </div>
  ) : null}
  
  {/* Show Mic if no text, no files staged, not modal open, not recording, not sending */}
  {messageText.trim() === '' && attachments.length === 0 && !showAttachmentModal && !isRecording && !isSendingAttachments ? (
    <button 
      onClick={handleToggleRecording} 
      className="group flex justify-center items-center rounded-full outline-none focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-indigo-400 dark:focus:ring-offset-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 w-8 h-8 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed" 
      title="Start recording" 
      aria-label="Start recording" 
      disabled={isSendingAttachments}
    >
      <MicrophoneIcon className="w-5 h-5 text-gray-500 dark:text-gray-400 group-hover:text-indigo-500 dark:group-hover:text-indigo-300" />
    </button>
  ) : isRecording ? (
    <button 
      onClick={handleToggleRecording} 
      className="group flex justify-center items-center rounded-full outline-none focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-red-500 dark:focus:ring-offset-gray-800 bg-red-500 hover:bg-red-600 w-8 h-8 transition-all duration-150 ease-in-out active:scale-110 disabled:opacity-50 disabled:cursor-not-allowed" 
      title="Stop recording" 
      aria-label="Stop recording" 
      disabled={isSendingAttachments}
    >
      <StopIcon className="w-4 h-4 text-white" />
    </button>
  ) : (
    <button 
      onClick={handleSendMessageClick} 
      className="group flex justify-center items-center outline-none rounded-full focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-indigo-500 dark:focus:ring-offset-gray-800 transition-all duration-150 ease-in-out w-8 h-8 bg-indigo-500 dark:bg-indigo-600 hover:bg-indigo-600 dark:hover:bg-indigo-500 active:scale-110 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-indigo-500 dark:disabled:hover:bg-indigo-600" 
      title="Send message" 
      aria-label="Send message" 
      disabled={(!messageText.trim() && attachments.length === 0) || isSendingAttachments}
    >
      <PaperAirplaneIcon className="w-4 h-4 text-white transform -rotate-45 -translate-x-px translate-y-px" />
    </button>
  )}
</div>
      </div>

      {showAttachmentModal && (
        <div role="dialog" aria-labelledby="attachment-modal-title" aria-modal="true" className="relative z-30">
           <div className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300 ease-in-out z-30" onClick={handleAttachmentModalClose} aria-hidden="true" ></div>
           <div className="fixed inset-0 z-50 overflow-y-auto"> <div className="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
              <div className="relative w-full max-w-lg bg-white dark:bg-gray-800 rounded-lg pt-5 pb-6 shadow-xl transform transition-all duration-300 ease-in-out sm:my-8" onClick={(e) => e.stopPropagation()} >
                  {/* Modal Header: Show Sending status if uploading */}
                  <h3 id="attachment-modal-title" className="text-lg font-semibold leading-6 text-gray-900 dark:text-gray-100 px-5 mb-3 text-left flex items-center">
                      {isSendingAttachments && <LoadingSpinner />}
                      {isSendingAttachments ? 'Sending Attachments...' : `Review Attachments (${attachments.length})`}
                  </h3>
                  {/* Attachments List - Renders local state including progress */}
                  <div className="scrollbox max-h-[40vh] sm:max-h-[50vh] overflow-y-auto mb-4 border-y border-gray-200 dark:border-gray-700 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent" >
                      {attachments.length > 0 ? attachments.map(att => (
                          <div key={att.id} className={`flex items-center px-5 py-3 border-b border-gray-100 dark:border-gray-700/50 last:border-b-0 transition-colors duration-150 ${att.status === 'failed' || att.status === 'cancelled' ? 'bg-red-50 dark:bg-red-900/30' : 'hover:bg-gray-50 dark:hover:bg-gray-700/30'}`}>
                              {/* Icon */}
                              <div className="w-8 h-8 mr-4 rounded-md flex-shrink-0 flex justify-center items-center bg-gray-100 dark:bg-gray-600">
                                  {att.status === 'uploading' ? <LoadingSpinner className="w-5 h-5" /> : att.status === 'uploaded' ? <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 text-green-600 dark:text-green-400"> <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.643a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.06l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" /> </svg> : <AttachmentIcon type={att.type} className="w-5 h-5" />}
                              </div>
                              {/* Info & Progress */}
                              <div className="flex-grow text-left mr-3 overflow-hidden min-w-0">
                                  <p className={`outline-none text-sm text-gray-800 dark:text-gray-100 font-medium leading-tight tracking-tight truncate`} title={att.name}> {att.name} </p>
                                  <p className="mt-0.5 outline-none text-xs text-gray-500 dark:text-gray-400 font-normal leading-tight tracking-tight" tabIndex="-1">
                                       {att.displaySize}
                                       {att.status === 'pending' && ' - Pending'}
                                       {att.status === 'uploading' && ` - Uploading (${att.progress}%)`}
                                       {att.status === 'uploaded' && ' - Uploaded'}
                                       {att.status === 'failed' && ` - Failed`}
                                       {att.status === 'cancelled' && ' - Cancelled'}
                                  </p>
                                  {att.status === 'uploading' && <ProgressBar progress={att.progress} />}
                                   {att.status === 'failed' && att.error && (
                                       <p className="mt-0.5 outline-none text-xs text-red-600 dark:text-red-400 font-normal leading-tight tracking-tight truncate">
                                           {att.error}
                                       </p>
                                   )}
                              </div>
                              {/* Actions */}
                              <div className="flex flex-shrink-0 items-center">
                                  {att.status === 'failed' && (
                                     <button onClick={() => uploadAttachment(att)} className="group flex justify-center items-center rounded-full outline-none focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-indigo-400 dark:focus:ring-offset-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 w-7 h-7 mr-1 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed" title="Retry Upload" disabled={isSendingAttachments} >
                                         <ArrowPathIcon className="w-4 h-4 text-gray-500 dark:text-gray-400 group-hover:text-indigo-500 dark:group-hover:text-indigo-300" />
                                     </button>
                                  )}
                                  {att.status !== 'uploaded' && att.status !== 'uploading' && (
                                      <>
                                      <button onClick={() => handleReplaceAttachment(att.id)} className="group flex justify-center items-center rounded-full outline-none focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-indigo-400 dark:focus:ring-offset-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 w-7 h-7 mr-1 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed" title="Replace (Not Implemented)" disabled={isSendingAttachments} >
                                          <ArrowPathIcon className="w-4 h-4 text-gray-500 dark:text-gray-400 group-hover:text-indigo-500 dark:group-hover:text-indigo-300" />
                                      </button>
                                      <button onClick={() => handleDeleteAttachment(att.id)} className="group flex justify-center items-center rounded-full outline-none focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-red-500 dark:focus:ring-offset-gray-800 hover:bg-red-100 dark:hover:bg-red-500/20 w-7 h-7 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed" title="Delete" disabled={isSendingAttachments && att.status !== 'failed' && att.status !== 'cancelled'} >
                                          <CloseIcon className="w-4 h-4 text-gray-500 dark:text-gray-400 group-hover:text-red-600 dark:group-hover:text-red-400" />
                                      </button>
                                      </>
                                  )}
                              </div>
                          </div>
                      )) : ( <p className="text-center text-sm text-gray-500 dark:text-gray-400 py-8">No files selected. Click 'Add Files' to choose files.</p> )}
                  </div>
                  {attachments.length > 0 && ( <div className="px-5 mb-5"> <input type="text" value={attachmentCaption} onChange={(e) => setAttachmentCaption(e.target.value)} className="block w-full h-9 px-3 py-1.5 rounded-md text-sm outline-none placeholder:text-gray-500 dark:placeholder:text-gray-400/80 focus:outline-none focus:ring-1 focus:ring-indigo-400 dark:focus:ring-indigo-500 transition duration-150 ease-out text-black dark:text-white bg-gray-100 dark:bg-gray-700/60 hover:bg-gray-200/70 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed" placeholder="Add a caption (optional)" aria-label="Attachment caption" disabled={isSendingAttachments} /> </div> )}
                  {/* Modal Actions */}
                  <div className="flex w-full px-5 items-center border-t border-gray-200 dark:border-gray-700 pt-4">
                     <div className="flex-grow flex justify-start">
                         <button onClick={handleAddAttachmentClick} tabIndex="0" className="group py-1.5 px-3 inline-flex items-center justify-center rounded-md transition-colors duration-150 ease-out outline-none text-sm hover:bg-indigo-50 dark:hover:bg-gray-700 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-indigo-400 text-indigo-600 dark:text-indigo-300 disabled:opacity-50 disabled:cursor-not-allowed" disabled={isSendingAttachments} >
                            <PlusIcon className="w-4 h-4 mr-1.5" />
                             Add Files
                         </button>
                     </div>
                     <button onClick={handleAttachmentModalClose} tabIndex="0" className="group py-1.5 px-4 inline-flex justify-center items-center rounded-md transition-colors duration-150 ease-out outline-none text-sm hover:bg-gray-100 dark:hover:bg-gray-700 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-400 text-gray-700 dark:text-gray-300 mr-3 disabled:opacity-50 disabled:cursor-not-allowed" disabled={isSendingAttachments && attachments.some(att => att.status === 'uploading')} > Cancel </button>
                      <button onClick={handleSendAttachments} tabIndex="0" disabled={!attachments.some(att => att.status === 'pending' || att.status === 'failed' || att.status === 'cancelled') || isSendingAttachments} className="group py-1.5 px-4 inline-flex justify-center items-center rounded-md transition-all duration-150 ease-out outline-none text-sm font-medium bg-indigo-500 dark:bg-indigo-600 hover:bg-indigo-600 dark:hover:bg-indigo-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-indigo-500 dark:focus-visible:ring-offset-gray-800 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-indigo-500 dark:disabled:hover:bg-indigo-600" >
                        {isSendingAttachments ? (
                             <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`animate-spin text-white mr-2`}> <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"></path> </svg>
                        ) : (
                             <PaperAirplaneIcon className="w-4 h-4 mr-2 text-white transform -rotate-45 -translate-x-px translate-y-px" />
                        )}
                        <p className="text-white leading-4"> Send {attachments.length > 0 && `(${attachments.length})`} </p>
                      </button>
                  </div>
              </div>
           </div></div>
        </div>
      )}

    </div>
  );
};

export default MessageInput;